'use client'

import { useFieldValidations } from 'country-configuration/services/client/useFieldValidations'
import { FormProvider, useForm } from 'form-components'
import { EmailInput } from 'form-components/components/EmailInput/EmailInput'
import { OrderInput } from 'form-components/components/OrderInput/OrderInput'
import { SubmitButton } from 'form-components/components/SubmitButton/SubmitButton'
import { useNavigation } from 'hooks/useNavigation/useNavigation'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import {
	EMAIL_INPUT_VALIDATION,
	ORDER_INPUT_VALIDATION,
	SearchOrderFormFieldIds,
	SearchOrderFormFieldNames,
	defaultValues,
} from 'my-purchases/constants/form'
import { PURCHASE_DETAIL_PATHNAME } from 'my-purchases/constants/paths'
import { useGuestLogin } from 'my-purchases/hooks/useGuestLogin'
import type { SearchOrderFormValues } from 'my-purchases/types/searchOrderForm'
import { sendOnSearchOrder } from 'my-purchases/utils/analytics/analyticsEvents'
import { useState } from 'react'
import { GUEST_EMAIL_CACHE_KEY } from 'utils/useGuestEmail/useGuestEmail'
import { setToSessionStorage } from 'utils/webStorage/sessionStorage'

import { Header } from '../Header/Header'

import styles from './SearchOrderForm.module.scss'

export function SearchOrderForm(): React.ReactNode {
	const [errorMessage, setErrorMessage] = useState('')
	const { t } = useLabels()
	const { navigate } = useNavigation()
	const { validateGuestLogin } = useGuestLogin()
	const { email } = useFieldValidations()

	const formMethods = useForm({
		defaultValues,
		mode: 'onTouched',
	})
	const { handleSubmit } = formMethods

	const onSubmit = async (data: SearchOrderFormValues) => {
		sendOnSearchOrder()
		try {
			const response = await validateGuestLogin({
				orderId: data.order,
				guestEmail: data.email,
			})
			if (!!response) {
				setToSessionStorage(GUEST_EMAIL_CACHE_KEY, data.email)
				navigate(
					`${PURCHASE_DETAIL_PATHNAME}?type=online&orderId=${data.order.toUpperCase()}`
				)
			}
		} catch (error) {
			setErrorMessage(t('myPurchases.login.tabNotRegisted.order.errormesagge'))
		}
		return false
	}

	return (
		<>
			<Header errorMessage={errorMessage} />
			<FormProvider {...formMethods}>
				<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
					<EmailInput
						name={SearchOrderFormFieldNames.Email}
						id={SearchOrderFormFieldIds.Email}
						rules={{
							...email,
							regex: EMAIL_INPUT_VALIDATION.regex,
						}}
						data-testid={SearchOrderFormFieldIds.Email}
					/>
					<OrderInput
						name={SearchOrderFormFieldNames.Order}
						id={SearchOrderFormFieldIds.Order}
						rules={ORDER_INPUT_VALIDATION}
						data-testid={SearchOrderFormFieldIds.Order}
						hintMessage={t('myPurchases.login.order.errormessage')}
						forceHint
					/>

					<SubmitButton
						id={SearchOrderFormFieldIds.SubmitButton}
						data-testid={SearchOrderFormFieldIds.SubmitButton}
						className={styles.button}
						secondary
					>
						{t('myPurchases.login.searchOrder.button')}
					</SubmitButton>
				</form>
			</FormProvider>
		</>
	)
}
